import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useAppSettingsStore } from '../stores/appSettingsStore';
import { complianceApi } from '../utils/api';

/**
 * Hook that handles:
 * - Session timeout (absolute max session duration)
 * - Inactivity logout (auto-logout after idle period)
 * - Logout warning (alert before auto-logout)
 */
export function useSessionTimeout() {
  const { isAuthenticated, logout, user } = useAuthStore();
  const settings = useAppSettingsStore((s) => s.settings);
  const [compliancePolicy, setCompliancePolicy] = useState<any>(null);
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningShown = useRef(false);

  useEffect(() => {
    if (isAuthenticated && !user?.recovery_scope) {
      complianceApi.getPolicies()
        .then(res => setCompliancePolicy(res.data))
        .catch(() => {});
    }
  }, [isAuthenticated, user?.recovery_scope]);

  const doLogout = useCallback(() => {
    warningShown.current = false;
    logout();
    window.location.href = '/login';
  }, [logout]);

  const resetInactivityTimer = useCallback(() => {
    // Determine timeout threshold
    let enabled = settings.inactivity_logout_enabled;
    let timeoutMinutes = settings.session_timeout_minutes;

    if (compliancePolicy && compliancePolicy.idle_timeout_minutes) {
      enabled = true; // Always enabled if compliance policy enforces it
      timeoutMinutes = Math.min(timeoutMinutes, compliancePolicy.idle_timeout_minutes);
    }

    if (!enabled || !isAuthenticated) return;

    // Clear existing timers
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);
    warningShown.current = false;

    const timeoutMs = timeoutMinutes * 60 * 1000;
    // Die in der Administration konfigurierte Vorwarnzeit gilt auch im Client.
    // Bei sehr kurzen Sessions bleibt mindestens die Hälfte der Laufzeit für aktive Arbeit.
    const configuredWarningMs = Math.max(1, Number(settings.logout_warning_minutes || 1)) * 60 * 1000;
    const warningMs = Math.min(configuredWarningMs, timeoutMs / 2);

    // Set warning timer (fires warningMs before logout)
    if (timeoutMs > warningMs) {
      warningTimer.current = setTimeout(() => {
        if (!warningShown.current) {
          warningShown.current = true;
          // Show a non-blocking notification
          const stay = window.confirm(
            `Sie werden in ${Math.max(1, Math.round(warningMs / 60000))} Minute(n) wegen Inaktivität abgemeldet.\n\nKlicken Sie OK um eingeloggt zu bleiben.`
          );
          if (stay) {
            resetInactivityTimer();
          }
        }
      }, timeoutMs - warningMs);
    }

    // Set logout timer
    inactivityTimer.current = setTimeout(() => {
      doLogout();
    }, timeoutMs);
  }, [settings.inactivity_logout_enabled, settings.session_timeout_minutes, settings.logout_warning_minutes, compliancePolicy, isAuthenticated, doLogout]);

  useEffect(() => {
    const isTimeoutEnabled = settings.inactivity_logout_enabled || (compliancePolicy && compliancePolicy.idle_timeout_minutes);
    if (!isAuthenticated || !isTimeoutEnabled) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    const handler = () => resetInactivityTimer();

    events.forEach((event) => document.addEventListener(event, handler, { passive: true }));
    resetInactivityTimer();

    return () => {
      events.forEach((event) => document.removeEventListener(event, handler));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      if (warningTimer.current) clearTimeout(warningTimer.current);
    };
  }, [isAuthenticated, settings.inactivity_logout_enabled, compliancePolicy, resetInactivityTimer]);
}
